import { onAuthStateChanged, User } from 'firebase/auth';
import { auth } from '../sp-firebase/firebase';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

async function waitForFirebaseUser(timeoutMs: number = 5000): Promise<User | null> {
  if (auth.currentUser) return auth.currentUser;
  return new Promise((resolve) => {
    let resolved = false;
    let unsubscribe: (() => void) | null = null;
    const timer = setTimeout(() => {
      if (resolved) return;
      resolved = true;
      if (unsubscribe) unsubscribe();
      resolve(null);
    }, timeoutMs);
    unsubscribe = onAuthStateChanged(auth, (user) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      if (unsubscribe) unsubscribe();
      resolve(user);
    });
  });
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const user = await waitForFirebaseUser();
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export interface CommodexBudgetRatio {
  formulaId: number;
  formulaCode: string;
  periodMonth: string; // YYYY-MM-DD (1st of month)
  ratio: number;
}

export async function getCommodexBudgetRatios(accountId: number): Promise<CommodexBudgetRatio[]> {
  const headers = await getAuthHeaders();
  const url = `${API_URL}/api/v2/commodex/budget-ratios?accountId=${accountId}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch commodex budget ratios');
  }
  const data = await response.json();
  return data.ratios || [];
}

export interface SetCommodexBudgetRatioParams {
  accountId: number;
  formulaId: number;
  periodMonth: string; // YYYY-MM-01
  ratio: number;
}

export async function setCommodexBudgetRatio(params: SetCommodexBudgetRatioParams): Promise<void> {
  const headers = await getAuthHeaders();
  const url = `${API_URL}/api/v2/commodex/budget-ratios`;
  const response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(params) });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to save commodex budget ratio');
  }
}

// ─── Beans budget price (per base material, per terminal maturity month) ────────

export interface CommodexBudgetPrice {
  baseMaterialCode: string;
  periodMonth: string; // YYYY-MM-DD (1st of month)
  price: number;
  currency: string | null;
}

export async function getCommodexBudgetPrices(accountId: number): Promise<CommodexBudgetPrice[]> {
  const headers = await getAuthHeaders();
  const url = `${API_URL}/api/v2/commodex/budget-prices?accountId=${accountId}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to fetch commodex budget prices');
  }
  const data = await response.json();
  return data.prices || [];
}

export interface SetCommodexBudgetPriceParams {
  accountId: number;
  baseMaterialCode: string;
  periodMonth: string; // YYYY-MM-01
  price: number;
  currency?: string | null;
}

export async function setCommodexBudgetPrice(params: SetCommodexBudgetPriceParams): Promise<void> {
  const headers = await getAuthHeaders();
  const url = `${API_URL}/api/v2/commodex/budget-prices`;
  const response = await fetch(url, { method: 'PUT', headers, body: JSON.stringify(params) });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to save commodex budget price');
  }
}
